/**
 * InvestorFitCard — "Personalized Investor Fit" section for DealView.
 *
 * This is a PERSONALIZATION LAYER. It never modifies or replaces:
 *   - RendPleX Intelligence Score (deal quality)
 *   - RendPleX Investment Rating (universal classification)
 *
 * It shows how well the current property matches the user's own strategy.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { User, Check, AlertTriangle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InvestorFitCard({ dealId, onProfileLoaded }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) return;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/investor-profile/fit/${dealId}`, {
        withCredentials: true,
        headers: { 'Accept-Language': i18n.language?.startsWith('fr') ? 'fr' : 'en' },
      })
      .then((r) => {
        setFit(r.data);
        if (onProfileLoaded) onProfileLoaded(r.data);
      })
      .catch(() => setFit(null))
      .finally(() => setLoading(false));
  }, [dealId, i18n.language, onProfileLoaded]);

  if (loading || !fit) return null;

  // No profile yet — show setup CTA
  if (!fit.has_profile || fit.insufficient_profile) {
    return (
      <div data-testid="investor-fit-empty" className="bg-gradient-to-br from-[#45C04A]/10 via-white to-[#12355B]/5 dark:from-[#45C04A]/10 dark:via-[#131824] dark:to-[#12355B]/10 border-2 border-dashed border-[#45C04A]/40 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#45C04A] to-[#65D95A] flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-1">
              {t('investorProfile.fit.title')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{t('investorProfile.fit.noProfile')}</p>
            <Button data-testid="setup-investor-profile-cta" onClick={() => navigate('/profile')}
              className="bg-[#12355B] hover:bg-[#1565C0] dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl">
              {t('investorProfile.fit.setupProfile')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const score = fit.score ?? 0;
  const bandColor =
    score >= 75
      ? { ring: '#45C04A', text: 'text-[#45C04A]', bg: 'bg-[#45C04A]/10' }
      : score >= 50
      ? { ring: '#F59E0B', text: 'text-amber-500', bg: 'bg-amber-500/10' }
      : { ring: '#EF4444', text: 'text-red-500', bg: 'bg-red-500/10' };

  return (
    <div
      data-testid="investor-fit-card"
      className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center flex-shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">
            {t('investorProfile.fit.title')}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{t('investorProfile.fit.subtitle')}</p>
        </div>
      </div>

      {/* Score */}
      <div className="flex items-center gap-6 mb-6">
        <div className="relative w-24 h-24 flex-shrink-0">
          <svg className="w-24 h-24 -rotate-90" viewBox="0 0 96 96">
            <circle cx="48" cy="48" r="42" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="6" />
            <circle cx="48" cy="48" r="42" fill="none" stroke={bandColor.ring} strokeWidth="6" strokeLinecap="round"
              strokeDasharray={`${(score / 100) * 264} 264`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span data-testid="investor-fit-score" className={`text-2xl font-primary font-extrabold ${bandColor.text}`}>{score}</span>
            <span className="text-[10px] text-slate-400 -mt-1">{t('investorProfile.fit.outOf')}</span>
          </div>
        </div>
        <div>
          <p data-testid="investor-fit-band" className={`text-lg font-primary font-bold ${bandColor.text} mb-1`}>{fit.band}</p>
          {fit.summary && (
            <p
              className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: fit.summary.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }}
            />
          )}
        </div>
      </div>

      {/* Matches / Warnings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {fit.matches?.length > 0 && (
          <div data-testid="investor-fit-matches">
            <p className="text-xs uppercase tracking-wider font-semibold text-[#45C04A] mb-2">
              {t('investorProfile.fit.matches')}
            </p>
            <ul className="space-y-2">
              {fit.matches.map((m, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <Check className="w-4 h-4 text-[#45C04A] flex-shrink-0 mt-0.5" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {fit.warnings?.length > 0 && (
          <div data-testid="investor-fit-warnings">
            <p className="text-xs uppercase tracking-wider font-semibold text-amber-500 mb-2">
              {t('investorProfile.fit.warnings')}
            </p>
            <ul className="space-y-2">
              {fit.warnings.map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Recommendation */}
      {fit.recommendation && (
        <div data-testid="investor-fit-recommendation" className="mt-6 pt-5 border-t border-slate-100 dark:border-slate-800">
          <p className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
            {t('investorProfile.fit.recommendation')}
          </p>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{fit.recommendation}</p>
        </div>
      )}
    </div>
  );
}
